import React from 'react';

import tracePattern from './assets/6a-steel300-1057x369@3x.png';
import { cn } from 'src/component/ds/utils';
import { useTranslation } from 'src/i18n';

export interface FacilityMapProps {
  /*
   * Omit for the trace pattern, the temporary default until facility image
   * upload exists. Pass null for no banner at all.
   */
  src?: string | null;
  alt?: string;
  /** Overlay cards, pinned to the bottom of the map. */
  children?: React.ReactNode;
  className?: string;
}

/*
 * The space for the facility map: a replaceable image, cropped to a fixed
 * aspect ratio so any upload leaves the layout alone. The design's overlay
 * cards land on top of this container later.
 */
export default function FacilityMap ({ src = tracePattern, alt, children, className }: FacilityMapProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'ds-relative ds-w-full ds-overflow-hidden ds-bg-muted',
        // 1200 x 369.055 with a bottom border, per Figma (node 1799:29539).
        'ds-aspect-[517/159] ds-border-b ds-border-solid ds-border-border',
        /* The aspect ratio alone would grow the map without bound on wide
         * monitors, pushing the cards below the fold. Cap it at the height the
         * ratio gives at Figma's 1200px width and let the image crop instead. */
        'ds-max-h-[369px]',
        className
      )}
      data-testid='facility-map'
    >
      {src
        ? (
          <img
            src={src}
            alt={alt ?? t('dashboard.facilityMapAlt')}
            className='ds-h-full ds-w-full ds-object-cover ds-object-top'
          />
        )
        : (
          /* Deliberate no-banner state, for a client that opts out or an
           * upload that failed: the token background, nothing to announce. */
          <div
            role='presentation'
            className='ds-h-full ds-w-full ds-bg-muted'
            data-testid='facility-map-placeholder'
          />
        )}

      {children
        ? (
          <div
            className='ds-absolute ds-bottom-0 ds-left-0 ds-p-3.5'
            data-testid='facility-map-overlay'
          >
            {children}
          </div>
        )
        : null}
    </div>
  );
}
